import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../components/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/card';
import { Badge } from '../components/badge';
import { ChevronLeft, ArrowRight, Brain, CheckCircle, AlertTriangle, XCircle, Lightbulb, Target, Zap, Heart, Users, BarChart3, FileText, RotateCcw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import api from "../sever/Api"
import { generateAIReport } from "../sever/Api"
import { notifyTestCompletion } from "../lib/testUtils";

const quizData = {
  title: " Emotional Intelligence (EQ) Quiz",
  subtitle: "Discover your emotional intelligence strengths and areas for growth",
  sections: [
    {
      id: "self-awareness",
      title: " Self-Awareness",
      description: "Understanding your own emotions, triggers, and patterns",
      icon: Lightbulb,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      questions: [
        {
          id: 1,
          text: "When I feel stressed, I usually:",
          options: [
            "Notice it quickly and understand why",
            "Push through without reflecting",
            "Distract myself with humor or others",
            "Withdraw to avoid conflict",
          ],
        },
        {
          id: 2,
          text: "How often do you reflect on your emotions after a conversation?",
          options: [
            "Often — I review how I felt and why",
            "Rarely — I move on quickly",
            "If the other person seemed upset",
            "Only if I need to repair the relationship",
          ],
        },
        {
          id: 3,
          text: "I can recognize when I'm about to overreact:",
          options: [
            "Before it happens",
            "After it already happens",
            "If someone points it out",
            "I usually suppress my reaction",
          ],
        },
        {
          id: 4,
          text: "People say I:",
          options: [
            "Understand myself well",
            "Am calm under pressure",
            "Care about others' feelings",
            "Help resolve conflicts",
          ],
        },
        {
          id: 5,
          text: "When I feel a strong emotion, I:",
          options: [
            "Observe it and try to label it",
            "Try to hide it",
            "Ask myself why I feel it",
            "Take it out on someone",
          ],
        },
      ],
    },
    {
      id: "self-management",
      title: "Self-Management",
      description: "Controlling your emotions and adapting to situations",
      icon: Target,
      color: "text-green-600",
      bgColor: "bg-green-50",
      questions: [
        {
          id: 6,
          text: "When something doesn't go as planned:",
          options: [
            "I keep calm and look for a new path",
            "I complain or blame",
            "I try to inspire myself",
            "I talk to someone about it",
          ],
        },
        {
          id: 7,
          text: "If I get angry at work or school:",
          options: [
            "I stay composed and process it internally",
            "I let others know directly",
            "I explode or shut down",
            "I use humor or change the topic",
          ],
        },
        {
          id: 8,
          text: "I handle stress by:",
          options: [
            "Setting priorities and adjusting calmly",
            "Avoiding tasks",
            "Reflecting on what I can control",
            "Talking it out",
          ],
        },
        {
          id: 9,
          text: "Others see me as:",
          options: [
            "Even-tempered",
            "Sensitive and emotional",
            "Passionate but reactive",
            "Supportive and kind",
          ],
        },
        {
          id: 10,
          text: "When I receive criticism, I:",
          options: [
            "Pause, breathe, and consider it calmly",
            "React defensively",
            "Reflect on what they might be feeling",
            "Use it to fuel personal growth",
          ],
        },
      ],
    },
    {
      id: "motivation",
      title: " Motivation",
      description: "Using emotions to drive goals, energy, and resilience",
      icon: Zap,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      questions: [
        {
          id: 11,
          text: "When I set goals:",
          options: [
            "I stay focused even during setbacks",
            "I get discouraged easily",
            "I ask others to hold me accountable",
            "I reflect on why I started",
          ],
        },
        {
          id: 12,
          text: "I feel most alive when:",
          options: [
            "I'm pursuing a meaningful challenge",
            "I'm relaxing without stress",
            "I'm connecting with others",
            "I'm overcoming internal blocks",
          ],
        },
        {
          id: 13,
          text: "When something is difficult:",
          options: [
            "I feel motivated to push harder",
            "I procrastinate or avoid it",
            "I look for support",
            "I question my ability",
          ],
        },
        {
          id: 14,
          text: "Others would say I am:",
          options: [
            "Determined and persistent",
            "Cautious and reserved",
            "Emotionally intelligent",
            "Calm under pressure",
          ],
        },
        {
          id: 15,
          text: "I handle setbacks by:",
          options: [
            "Reframing them as opportunities to grow",
            "Ignoring them",
            "Getting help to stay on track",
            "Feeling overwhelmed and quitting",
          ],
        },
      ],
    },
    {
      id: "empathy",
      title: "Empathy (Social Awareness)",
      description: "Understanding others' emotions and perspectives",
      icon: Heart,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      questions: [
        {
          id: 16,
          text: "In a group, I tend to:",
          options: [
            "Notice who is uncomfortable or quiet",
            "Keep everyone focused on tasks",
            "Motivate others to try their best",
            "Reflect inwardly instead of outwardly",
          ],
        },
        {
          id: 17,
          text: "When someone is upset, I:",
          options: [
            "Try to understand how they're feeling",
            "Fix the problem quickly",
            "Cheer them up",
            "Avoid the situation",
          ],
        },
        {
          id: 18,
          text: "I am good at:",
          options: [
            "Reading facial expressions and tone",
            "Staying on track emotionally",
            "Managing conflict",
            "Reflecting on personal goals",
          ],
        },
        {
          id: 19,
          text: "Others say I:",
          options: [
            "Understand what people need",
            "Am always composed",
            "Inspire them with passion",
            "Keep emotions to myself",
          ],
        },
        {
          id: 20,
          text: "When someone shares a personal problem:",
          options: [
            "I listen without judgment",
            "I relate it to my own experience",
            "I suggest next steps",
            "I ignore it if I feel awkward",
          ],
        },
      ],
    },
    {
      id: "relationship-management",
      title: "Relationship Management",
      description: "Managing interactions, resolving conflicts, influencing others",
      icon: Users,
      color: "text-red-600",
      bgColor: "bg-red-50",
      questions: [
        {
          id: 21,
          text: "When working with others, I:",
          options: [
            "Try to build a strong emotional connection",
            "Stay quiet unless spoken to",
            "Focus on emotional balance",
            "Help manage tension",
          ],
        },
        {
          id: 22,
          text: "In a disagreement, I tend to:",
          options: [
            "Communicate clearly and calmly",
            "Avoid the conversation",
            "Reflect on what went wrong",
            "Try to win the argument",
          ],
        },
        {
          id: 23,
          text: "People would describe me as:",
          options: [
            "A natural communicator",
            "Thoughtful and quiet",
            "Someone who sees both sides",
            "Passionate but difficult in groups",
          ],
        },
        {
          id: 24,
          text: "When helping others through conflict, I:",
          options: [
            "Stay neutral and guide resolution",
            "Avoid taking sides",
            "Step away and reflect",
            "Feel emotionally drained",
          ],
        },
        {
          id: 25,
          text: "I build strong relationships by:",
          options: [
            "Communicating openly and supporting others",
            "Being highly productive",
            "Listening without interrupting",
            "Focusing mostly on my own performance",
          ],
        },
      ],
    },
  ],
};

// We no longer use a generic Likert scale. Each question provides its own A/B/C/D
// options that we render directly. Answers are stored as the selected option index (0-3).
const EmotionalIntelligenceTest = () => {
  const location = useLocation();
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [results, setResults] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportError, setAiReportError] = useState(null);
  const [aiReportText, setAiReportText] = useState("");
  const [showIntro, setShowIntro] = useState(true);
  const totalQuestions = quizData.sections.reduce((acc, section) => acc + section.questions.length, 0);
  const answeredQuestions = Object.keys(answers).length;
  const progressPercentage = Math.round((answeredQuestions / totalQuestions) * 100);
  const [autoNext, setAutoNext] = useState(false);
  const currentSectionData = quizData.sections[currentSection];
  const currentQuestionData = currentSectionData.questions[currentQuestion];

  // Calculate progress for each section
  const getSectionProgress = (sectionIndex) => {
    const section = quizData.sections[sectionIndex];
    const sectionQuestionIds = section.questions.map(q => q.id);
    const answeredInSection = sectionQuestionIds.filter(id => answers[id]).length;
    return {
      answered: answeredInSection,
      total: section.questions.length
    };
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
    setAutoNext(true);
  };
  const buildDetailedAnswers = () => {
    const detailedAnswers = [];
    quizData.sections.forEach((section) => {
      section.questions.forEach((question) => {
        const answer = answers[question.id];
        if (answer !== undefined) {
          detailedAnswers.push({
            questionId: question.id.toString(),
            questionText: question.text,
            selectedOptionId: String(answer),
            selectedOptionText: question.options[Number(answer)] ?? 'Not answered'
          });
        }
      });
    });
    return detailedAnswers;
  };
  const saveTestResult = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to save your results.");
        return;
      }

      const detailedAnswers = buildDetailedAnswers();

      await api.post("/api/test-results", {
        testType: "emotionalintelligence",
        answers: detailedAnswers,
        scores: results.sectionScores,
        overallScore: results.totalScore,
        overallPercentage: results.overallPercentage,
      });

      toast.success("Test results saved successfully!");
    } catch (error) {
      console.error("Error saving test result:", error);
      toast.error(error.response?.data?.message || "Failed to save test results");
    }
  };

  const fetchAIReport = async () => {
    try {
      setAiReportLoading(true);
      setAiReportError(null);
      const detailedAnswers = buildDetailedAnswers();
      const payload = {
        testType: 'emotionalintelligence',
        scores: results.sectionScores,
        answers: detailedAnswers,
        overallScore: results.totalScore,
        overallPercentage: results.overallPercentage,
      };
      const { report } = await generateAIReport(payload);
      setAiReportText(report);
    } catch (err) {
      setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
    } finally {
      setAiReportLoading(false);
    }
  };


  // Save results when they are first shown
  useEffect(() => {
    if (showResults && results) {
      saveTestResult();
      try {
        const testData = {
          scores: results.sectionScores,
          overallScore: results.overallPercentage,
          overallPercentage: results.overallPercentage,
          // Provide a simple label
          personalityType: results.overallPercentage >= 65 ? 'High EQ' : results.overallPercentage >= 50 ? 'Moderate EQ' : 'Developing EQ',
          testType: 'emotionalintelligence',
          completed: true
        };
        notifyTestCompletion('emotionalintelligence', testData);
      } catch (e) {
        // no-op
      }
    }
  }, [showResults, results]);

  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowResults(true);
    }
  }, [location.search]);
  
  useEffect(() => {
    if (autoNext) {
      handleNext();
      setAutoNext(false);
    }
    // eslint-disable-next-line
  }, [autoNext]);

  const calculateResults = (finalAnswers) => {
    const sectionScores = {};
    let totalScore = 0;
    let totalQuestions = 0;

    quizData.sections.forEach((section) => {
      let sectionScore = 0;
      section.questions.forEach((question) => {
        const answer = finalAnswers[question.id];
        if (answer !== undefined) {
          // Map selected option index (0-3) to scores (1-4)
          const answerIndex = Number(answer);
          const score = (Number.isNaN(answerIndex) ? 0 : answerIndex + 1);
          sectionScore += score;
          totalScore += score;
          totalQuestions++;
        }
      });
      sectionScores[section.id] = sectionScore;
    });

    const overallPercentage = Math.round((totalScore / (totalQuestions * 4)) * 100);

    return {
      sectionScores,
      totalScore,
      overallPercentage,
      totalQuestions
    };
  };

  const handleNext = () => {
    if (currentQuestion < currentSectionData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < quizData.sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentQuestion(0);
    } else {
      // Test completed, calculate and show results
      const calculatedResults = calculateResults(answers);
      setResults(calculatedResults);
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(quizData.sections[currentSection - 1].questions.length - 1);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const canGoNext =
    currentSection < quizData.sections.length - 1 || currentQuestion < currentSectionData.questions.length - 1;
  const isLastQuestion = currentSection === quizData.sections.length - 1 && currentQuestion === currentSectionData.questions.length - 1;
  const canGoPrevious = currentSection > 0 || currentQuestion > 0;

  const getLevel = (percentage) => {
    if (percentage >= 80) return { level: 'Very High', color: 'bg-red-500' }
    if (percentage >= 65) return { level: 'High', color: 'bg-orange-500' }
    if (percentage >= 50) return { level: 'Moderate', color: 'bg-yellow-500' }
    if (percentage >= 35) return { level: 'Low', color: 'bg-blue-500' }
    return { level: 'Very Low', color: 'bg-green-500' }
  }

  const getEQDescription = (sectionId, percentage) => {
    const section = quizData.sections.find(s => s.id === sectionId);
    if (!section) return "No description available.";

    const { level, color } = getLevel(percentage);
    const Icon = section.icon;

    return (
      <div className="flex items-center">
        <Icon className={`w-6 h-6 mr-2 ${color}`} />
        <p className="text-gray-700">
          Your {section.title.replace(' (Social Awareness)', '')} score is {percentage}% ({level}).
          This indicates your ability to {section.description.split(' ')[0]} is {level.toLowerCase()}.
          Your strengths in this area include {getEQStrengths(sectionId, percentage)[0]}.
          Your growth areas might be {getEQGrowthAreas(sectionId, percentage)[0]}.
        </p>
      </div>
    );
  };

  const getEQStrengths = (sectionId, percentage) => {
    const section = quizData.sections.find(s => s.id === sectionId);
    if (!section) return ["No specific strengths identified."];

    const strengths = [];
    section.questions.forEach(question => {
      const answer = answers[question.id];
      if (answer !== undefined) {
        const optionText = question.options[Number(answer)];
        if (optionText && optionText.includes("well") || optionText.includes("calm") || optionText.includes("strong") || optionText.includes("helpful") || optionText.includes("helpful")) {
          strengths.push(optionText);
        }
      }
    });
    return strengths.length > 0 ? strengths : ["No specific strengths identified."];
  };

  const getEQGrowthAreas = (sectionId, percentage) => {
    const section = quizData.sections.find(s => s.id === sectionId);
    if (!section) return ["No specific growth areas identified."];

    const growthAreas = [];
    section.questions.forEach(question => {
      const answer = answers[question.id];
      if (answer !== undefined) {
        const optionText = question.options[Number(answer)];
        if (optionText && optionText.includes("overreact") || optionText.includes("complaining") || optionText.includes("complaining") || optionText.includes("complaining") || optionText.includes("complaining")) {
          growthAreas.push(optionText);
        }
      }
    });
    return growthAreas.length > 0 ? growthAreas : ["No specific growth areas identified."];
  };

  const getEQCareerImpact = (sectionId, percentage) => {
    const section = quizData.sections.find(s => s.id === sectionId);
    if (!section) return "No specific career impact identified.";

    const impact = [];
    section.questions.forEach(question => {
      const answer = answers[question.id];
      if (answer !== undefined) {
        const optionText = question.options[Number(answer)];
        if (optionText && optionText.includes("goals") || optionText.includes("goals") || optionText.includes("goals") || optionText.includes("goals") || optionText.includes("goals")) {
          impact.push(optionText);
        }
      }
    });
    return impact.length > 0 ? impact.join(' ') : "No specific career impact identified.";
  };

  const getEQPersonalDevelopment = (sectionId, percentage) => {
    const section = quizData.sections.find(s => s.id === sectionId);
    if (!section) return "No specific personal development identified.";

    const development = [];
    section.questions.forEach(question => {
      const answer = answers[question.id];
      if (answer !== undefined) {
        const optionText = question.options[Number(answer)];
        if (optionText && optionText.includes("personal") || optionText.includes("personal") || optionText.includes("personal") || optionText.includes("personal") || optionText.includes("personal")) {
          development.push(optionText);
        }
      }
    });
    return development.length > 0 ? development.join(' ') : "No specific personal development identified.";
  };

  if (showIntro) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main Content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">
                    Emotional Intelligence Test
                  </CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Measure your ability to recognize, understand, and manage emotions effectively
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    {quizData.sections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <div key={section.id} className="space-y-2">
                          <h3 className={`font-semibold ${section.color}`}>
                            {section.title.replace(" (Social Awareness)", "")}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {section.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{totalQuestions} questions</strong> •{" "}
                      Gain insights into your
                      Emotional Intelligence
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      // Mark free test as used when user actually starts the test
                      try {
                        const userStr = localStorage.getItem('user');
                        const user = userStr ? JSON.parse(userStr) : null;
                        const isPremium = user && (user.role === 'premium' || user.role === 'admin');
                        if (!isPremium) {
                          localStorage.setItem('freeTestUsed', '1');
                        }
                      } catch (_) {}
                      setShowIntro(false);
                    }}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className="ml-0 md:ml-64 min-h-screen  ">
        <Navbar
          toggleSidebar={toggleSidebar}
        />
        {showResults && results && (
          <div className="min-h-screen p-4">
            <div className="mx-auto" style={{ width: '85%' }}>
              <Card className='bg-[#ffffff]'>
                <CardHeader className="text-center">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <BarChart3 className="w-8 h-8 text-green-600" />
                  </div>
                  <CardTitle className="text-3xl font-bold">Your Emotional Intelligence Results</CardTitle>
                  <CardDescription>Based on the EQ assessment model</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {quizData.sections.map((section) => {
                    const sectionScore = results.sectionScores[section.id];
                    const maxScore = section.questions.length * 4;
                    const percentage = Math.round((sectionScore / maxScore) * 100);
                    const { level, color } = getLevel(percentage);
                    return (
                      <div key={section.id} className="space-y-3">
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold text-lg">{section.title}</h3>
                          <div className="text-right">
                            <span className="font-bold">{sectionScore}/{maxScore}</span>
                            <span className={`ml-2 px-2 py-1 rounded text-xs text-white ${color}`}>{level}</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <p className="text-sm text-gray-600">{section.description}</p>
                      </div>
                    );
                  })}
                  <div className="flex gap-4 pt-6">
                    <Button onClick={() => setShowReport(true)} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">
                      <FileText className="mr-2 w-4 h-4" />
                      View Detailed Report
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowResults(false);
                        setShowReport(false);
                        setResults(null);
                        setAnswers({});
                        setCurrentSection(0);
                        setCurrentQuestion(0);
                      }}
                    >
                      <RotateCcw className="mr-2 w-4 h-4" />
                      Retake Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {showResults && results && showReport && (
          <div className="min-h-screen p-4">
            <div className="mx-auto space-y-6" style={{ width: '85%' }}>
              <Card className='bg-[#ffffff]'>
                <CardHeader>
                  <CardTitle className="text-2xl font-bold flex items-center">
                    <FileText className="mr-3 w-6 h-6" />
                    Detailed Emotional Intelligence Report
                  </CardTitle>
                  <CardDescription>Comprehensive analysis of your EQ assessment</CardDescription>
                </CardHeader>
              </Card>

              <Card className='bg-[#ffffff]'>
                <CardHeader>
                  <CardTitle className="text-xl font-semibold">We are collecting your results</CardTitle>
                  <CardDescription>Personalized insights</CardDescription>
                </CardHeader>
                <CardContent>
                  {aiReportLoading && (
                    <div className="flex items-center text-gray-600">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                      Generating your report...
                    </div>
                  )}
                  {aiReportError && (
                    <div className="text-red-600 text-sm mb-2">{aiReportError}</div>
                  )}
                  {!aiReportLoading && aiReportText && (
                    <div className="prose max-w-none whitespace-pre-wrap text-gray-800 text-sm">{aiReportText}</div>
                  )}
                  {!aiReportLoading && !aiReportText && (
                    <Button onClick={fetchAIReport} className="bg-blue-600 hover:bg-blue-700 text-white">Generate Report</Button>
                  )}
                </CardContent>
              </Card>

              {quizData.sections.map((section) => {
                const sectionScore = results.sectionScores[section.id];
                const maxScore = section.questions.length * 4;
                const percentage = Math.round((sectionScore / maxScore) * 100);
                const { level, color } = getLevel(percentage);
                const Icon = section.icon;
                return (
                  <Card className='bg-[#ffffff]' key={section.id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl">{section.title}</CardTitle>
                          <CardDescription>{section.description}</CardDescription>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">{sectionScore}/{maxScore}</div>
                          <span className={`px-3 py-1 rounded-full text-sm text-white ${color}`}>{level}</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Enhanced Content */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-2">Your EQ Profile</h4>
                        <p className="text-gray-700">{getEQDescription(section.id, percentage)}</p>
                      </div>

                      {/* Key Strengths & Growth Areas */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-semibold text-green-800 mb-2">Key Strengths</h4>
                          <ul className="space-y-1">
                            {getEQStrengths(section.id, percentage).map((strength, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <span className="text-green-700 text-sm">{strength}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-4">
                          <h4 className="font-semibold text-orange-800 mb-2">Growth Areas</h4>
                          <ul className="space-y-1">
                            {getEQGrowthAreas(section.id, percentage).map((area, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                <span className="text-orange-700 text-sm">{area}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Career & Personal Development */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-800 mb-2">Career Impact</h4>
                          <p className="text-blue-700 text-sm leading-relaxed">{getEQCareerImpact(section.id, percentage)}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4">
                          <h4 className="font-semibold text-purple-800 mb-2">Personal Development</h4>
                          <p className="text-purple-700 text-sm leading-relaxed">{getEQPersonalDevelopment(section.id, percentage)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              <div className="flex gap-4">
                <Button onClick={() => setShowReport(false)} variant="outline" className="flex-1 bg-[#ffffff]">
                  Back to Results
                </Button>
                <Button
                  onClick={() => {
                    setShowResults(false);
                    setShowReport(false);
                    setResults(null);
                    setAnswers({});
                    setCurrentSection(0);
                    setCurrentQuestion(0);
                  }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Retake Test
                </Button>
              </div>
            </div>
          </div>
        )}

        {!showResults && (
          <div className=" mx-auto mt-8" style={{ width: '85%' }}>
            {/* Header */}
            <div className="text-center mb-8">
              {/* <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div> */}
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{quizData.title}</h1>
              <p className="text-gray-600 max-w-2xl mx-auto">{quizData.subtitle}</p>
            </div>

            {/* Progress by Category */}
            <div className="mb-8 bg-white rounded-lg border border-gray-200 pt-6 px-6">
              <h3 className="font-semibold mb-3">Your Progress by Category</h3>
              <div className="grid grid-cols-5 gap-8 mb-8">
                {quizData.sections.map((section, index) => {
                  const progress = getSectionProgress(index);
                  const Icon = section.icon;

                  return (
                    <div
                      key={section.id}
                      className="text-center cursor-pointer transition-all duration-200 hover:opacity-80"
                      onClick={() => {
                        setCurrentSection(index);
                        setCurrentQuestion(0);
                      }}
                    >

                      <div className={`w-10 h-10 p-2 rounded-full w-fit mx-auto mb-1 ${section.bgColor}`} >
                        <Icon className={`w-6 h-6 ${section.color}`} />
                      </div>
                      <h3 className="font-semibold text-sm mb-1 text-gray-900">
                        {section.title.replace(' (Social Awareness)', '')}
                      </h3>
                      <div className="text-sm text-gray-500">
                        {progress.answered}/{progress.total}
                      </div>
                    </div>
                  );
                })}
              </div>


            </div>

            {/* Progress Bar */}
            <div className='mb-7'>

              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Question {currentSection * 5 + currentQuestion + 1} of {totalQuestions}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {progressPercentage}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>



            {/* Question Card */}
            <Card className="mb-6 bg-[#ffffff]">
              <CardHeader className="p-4 border-b">

                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">
                    {currentSectionData.title}
                  </CardTitle>
                  <p className="text-sm text-gray-500 ">{currentSectionData.description}</p>
                </div>
              </CardHeader>
              <CardContent className='p-5'>
                <h3 className="text-lg font-medium mb-6 text-gray-900">{currentQuestionData.text}</h3>
                <div className="space-y-3">
                  {currentQuestionData.options.map((optionText, optionIndex) => {
                    const isSelected = answers[currentQuestionData.id] === optionIndex;
                    return (
                      <button
                        key={optionIndex}
                        onClick={() => handleAnswerSelect(currentQuestionData.id, optionIndex)}
                        className={`w-full p-2 px-4 rounded-lg border-2 transition-all duration-200 text-left bg-white border-gray-200 hover:border-gray-300 text-gray-700 ${isSelected
                          ? "border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                          : "hover:bg-[rgb(197,199,203)] dark:hover:bg-[rgb(197,199,203)]"}
`}
                      >
                        <div >{optionText}</div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex justify-between items-center mb-7">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={!canGoPrevious}
                className="px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 "
              >
                {/* <ChevronLeft className="w-4 h-4" /> */}
                Previous
              </Button>
              {/* <div className="text-sm text-gray-500">
              Question {currentSection * 5 + currentQuestion + 1} of {totalQuestions}
            </div> */}
              {/* <Button
                onClick={handleNext}
                disabled={answers[currentQuestionData.id] === undefined}
                className="px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {!isLastQuestion ? (
                  <>
                    Next Question
                  </>
                ) : (
                  "Complete Quiz"
                )}
              </Button> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmotionalIntelligenceTest; 